import { formatDateTime, useLocale, useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@multaqa/ui/components/ui/sheet'
import { CopyIcon } from 'lucide-react'
import { useState } from 'react'
import type { AuditLogResource } from './api'
import { auditChangeValue } from './audit-change-value'
import { recordHref } from './audit-utils'

export function AuditDetailSheet({
  rows,
  open,
  onOpenChange,
  onHistory,
}: {
  rows: AuditLogResource[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onHistory: (event: AuditLogResource) => void
}) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const [copied, setCopied] = useState(false)
  const first = rows[0]
  if (!first) return null
  const href = recordHref(first.record.type, first.record.id)
  const changes = rows.flatMap((row) =>
    row.changes.map((change) => ({ ...change, actionKind: row.action_kind })),
  )
  const copyReference = async () => {
    if (first.request_id) await navigator.clipboard.writeText(first.request_id)
    setCopied(true)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="end"
        closeLabel={t('common:action.cancel')}
        className="max-w-xl max-sm:max-w-none"
        data-testid="audit-detail-sheet"
      >
        <SheetHeader>
          <SheetTitle>{first.record.label || first.action_label}</SheetTitle>
          <SheetDescription>
            {first.actor.name} · {formatDateTime(new Date(first.occurred_at), locale)}
          </SheetDescription>
        </SheetHeader>
        <SheetBody className="space-y-6">
          <section>
            <h3 className="mb-2 text-label text-muted-foreground">
              {t('companyAudit.detail.changes')}
            </h3>
            <div className="overflow-hidden rounded-md border border-border">
              <div className="grid grid-cols-2 bg-muted px-3 py-2 text-label font-medium">
                <span>{t('companyAudit.detail.old')}</span>
                <span>{t('companyAudit.detail.new')}</span>
              </div>
              {changes.map((change) => {
                const oldValue = auditChangeValue(change, 'old', locale)
                const newValue = auditChangeValue(change, 'new', locale)
                const showOld = change.actionKind !== 'created'
                const showNew = change.actionKind !== 'deleted'
                return (
                  <div
                    key={`${change.field}-${change.old ?? ''}-${change.new ?? ''}`}
                    className="grid grid-cols-2 gap-3 border-t border-border px-3 py-3 text-small"
                  >
                    <span>
                      <span className="mb-1 block text-muted-foreground">{change.label}</span>
                      {change.masked ? (
                        t('companyAudit.timeline.hidden')
                      ) : showOld ? (
                        <span className={oldValue === null ? 'text-muted-foreground' : undefined}>
                          {oldValue ?? '—'}
                        </span>
                      ) : null}
                    </span>
                    <span className="self-end">
                      {change.masked ? (
                        t('companyAudit.timeline.hidden')
                      ) : showNew ? (
                        <span className={newValue === null ? 'text-muted-foreground' : undefined}>
                          {newValue ?? '—'}
                        </span>
                      ) : null}
                    </span>
                  </div>
                )
              })}
            </div>
          </section>
          <section className="grid gap-3 rounded-md border border-border p-4 text-small">
            <p>
              <span className="text-muted-foreground">{t('companyAudit.detail.actor')}:</span>{' '}
              {first.actor.name}
            </p>
            <p>
              <span className="text-muted-foreground">{t('companyAudit.detail.date')}:</span>{' '}
              {formatDateTime(new Date(first.occurred_at), locale)}
            </p>
            {first.request_id ? (
              <p className="flex items-center gap-2">
                <span className="text-muted-foreground">{t('companyAudit.detail.reference')}:</span>
                <bdi className="tabular-nums">{first.request_id}</bdi>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('companyAudit.detail.copyReference')}
                  onClick={() => void copyReference()}
                >
                  <CopyIcon className="size-4" />
                </Button>
                {copied ? <span>{t('companyAudit.detail.copied')}</span> : null}
              </p>
            ) : null}
            {first.ip_address ? (
              <p>
                <span className="text-muted-foreground">{t('companyAudit.detail.ip')}:</span>{' '}
                <bdi>{first.ip_address}</bdi>
              </p>
            ) : null}
            <p>
              <span className="text-muted-foreground">{t('companyAudit.detail.device')}:</span>{' '}
              {first.user_agent || t('companyAudit.detail.unknown')}
            </p>
            {first.acting_for_company_id ? (
              <p>
                <span className="text-muted-foreground">{t('companyAudit.detail.actingFor')}:</span>{' '}
                <bdi>{first.acting_for_company_id}</bdi>
              </p>
            ) : null}
          </section>
          <div className="flex flex-wrap gap-2">
            {href ? (
              <Button asChild variant="outline">
                <a href={href}>{t('companyAudit.detail.openRecord')}</a>
              </Button>
            ) : null}
            <Button
              variant="outline"
              onClick={() => {
                onHistory(first)
                onOpenChange(false)
              }}
            >
              {t('companyAudit.detail.showHistory')}
            </Button>
          </div>
          {rows.length > 1 ? (
            <section>
              <h3 className="mb-2 text-label text-muted-foreground">
                {t('companyAudit.detail.related')}
              </h3>
              <ul className="grid gap-2">
                {rows.map((row) => (
                  <li key={row.id} className="rounded border border-border p-3 text-small">
                    {row.action_label} · {formatDateTime(new Date(row.occurred_at), locale)}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </SheetBody>
      </SheetContent>
    </Sheet>
  )
}
